import { api } from './client';
import type { CEFRLevel } from './courses';

export type SpeakingBooking = {
  id: string;
  room_id: string;
  student_id: string;
  booked_at: string;
  status: 'booked' | 'attended' | 'no_show' | 'cancelled';
};

export type SpeakingSessionRating = {
  id: string;
  room_id: string;
  student_id: string;
  rating: number;
  comment?: string;
  created_at: string;
};

export type SpeakingRoom = {
  id: string;
  title: string;
  topic?: string;
  host_id?: string;
  curator_id?: string;
  scheduled_at: string;
  duration_min: number;
  max_participants: number;
  current_bookings?: number;
  level?: CEFRLevel;
  language?: string;
  meet_link?: string;
  status: 'scheduled' | 'active' | 'finished' | 'cancelled';
};

// A booking enriched with its room and the student's own rating, returned by
// GET /student/speaking/history.
export type SpeakingHistoryEntry = SpeakingBooking & {
  room?: SpeakingRoom;
  rating?: SpeakingSessionRating;
};

export type AISpeakingSession = {
  id: string;
  student_id: string;
  topic: string;
  status: 'active' | 'completed';
  cefr_level?: CEFRLevel;
  total_duration?: number;
  created_at: string;
  updated_at?: string;
};

export type AIAnalysis = {
  session_id: string;
  duration_min: number;
  topic: string;
  analysis: {
    cefr_level: CEFRLevel;
    vocabulary_range: string;
    grammar_accuracy: string;
    fluency: string;
    pronunciation_estimate: string;
    strengths: string;
    improvements: string;
    recommendations: string;
  };
};

export const speakingApi = {
  // ----- Rooms (peer-to-peer) -----
  async listRooms(params?: { level?: CEFRLevel; from?: string; to?: string }) {
    const { data } = await api.get('/student/speaking/rooms', { params });
    return data as SpeakingRoom[];
  },
  async getRoom(id: string) {
    const { data } = await api.get(`/student/speaking/rooms/${id}`);
    return data as SpeakingRoom;
  },
  async bookRoom(id: string) {
    await api.post(`/student/speaking/rooms/${id}/book`);
  },
  async cancelBooking(id: string) {
    await api.delete(`/student/speaking/rooms/${id}/book`);
  },
  async joinRoom(id: string) {
    const { data } = await api.get(`/student/speaking/rooms/${id}/join`);
    return data as { meet_link: string };
  },
  async rateRoom(id: string, rating: number, comment?: string) {
    await api.post(`/student/speaking/rooms/${id}/rate`, { rating, comment });
  },
  async history() {
    const { data } = await api.get('/student/speaking/history');
    return data as SpeakingHistoryEntry[];
  },

  // ----- AI trainer -----
  async startAI(topic: string) {
    const { data } = await api.post('/student/speaking/ai/start', { topic });
    return data as AISpeakingSession;
  },
  async sendAIMessage(sessionId: string, message: string) {
    const { data } = await api.post(`/student/speaking/ai/${sessionId}/message`, { message });
    return data as { response: string };
  },
  async endAI(sessionId: string) {
    const { data } = await api.post(`/student/speaking/ai/${sessionId}/finish`);
    return data as AIAnalysis;
  },
  async listAISessions() {
    const { data } = await api.get('/student/speaking/ai/history');
    return data as AISpeakingSession[];
  },

  // Voice IO (OpenAI Whisper + TTS via speaking-service)
  async transcribe(audioBlob: Blob, filename = 'audio.webm'): Promise<{ text: string }> {
    const form = new FormData();
    form.append('file', audioBlob, filename);
    const { data } = await api.post('/student/speaking/ai/transcribe', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data as { text: string };
  },
  async synthesize(text: string): Promise<Blob> {
    const resp = await api.post('/student/speaking/ai/speak', { text }, { responseType: 'blob' });
    return resp.data as Blob;
  },

  /**
   * Pronunciation scoring (P4.4). Returns a 0..100 score plus the lists of
   * correctly-spoken / missed / extra words so the UI can highlight each.
   */
  async scorePronunciation(audio: Blob, expected: string, filename = 'pronunciation.webm') {
    const form = new FormData();
    form.append('file', audio, filename);
    form.append('expected', expected);
    const { data } = await api.post('/student/speaking/ai/pronounce', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data as {
      transcript: string;
      expected: string;
      score: number;
      correct_words: string[];
      missing_words: string[];
      extra_words: string[];
    };
  },
};
